/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { AfterContentChecked, Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import {
    DataReportCollection,
    DataReportLaunchOptions,
    FeatureReportCollection,
    FeatureReportLaunchOptions,
    ReportDefinition,
    ReportLaunchOptions,
    ReportTypeValue,
} from '../../state/report.model';
import { ReportState } from '../../state/report.state';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { LoggerService } from '../../../shared/services/logger.service';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { SelectReport } from '../../state/report.actions';
import { isEqual } from 'lodash-es';
import { IsolatedReportService } from '../../services/isolatedReport.service';
import { DashGisUtil } from '../../../shared/dash-gis-util';

@Component({
    selector: 'dash-gis-report-selector',
    templateUrl: './report-selector.component.html',
    styleUrls: ['./report-selector.component.scss'],
    providers: [IsolatedReportService],
    encapsulation: ViewEncapsulation.None,
})
export class ReportSelectorComponent implements OnInit, AfterContentChecked {
    @Output() recordCount = new EventEmitter<number>();
    @Output() reportTitle = new EventEmitter<string>();

    @Input() launchOptions: ReportLaunchOptions[];
    @Input() reportType: ReportTypeValue;
    @Input() uid: string;

    @Select(ReportState.selectedFeatureReport) selectedFeatureReports$: Observable<
        (string) => FeatureReportLaunchOptions
    >;
    @Select(ReportState.activeFeatureReports) activeFeatureReports$: Observable<FeatureReportCollection>;
    @Select(ReportState.selectedDataReport) selectedDataReport$: Observable<DataReportLaunchOptions>;
    @Select(ReportState.activeDataReports) activeDataReports$: Observable<DataReportCollection>;

    constructor(private logger: LoggerService, private isolatedReport: IsolatedReportService, private store: Store) {}

    ngOnInit() {
        if (!this.reportType) {
            throw new Error('ReportSelectorComponent: attribute [reportType] is required');
        }
        if (!this.launchOptions) {
            throw new Error('ReportSelectorComponent: attribute [launchOptions] is required');
        }

        // clear report title when history action results in no selected report
        this.selectedReport$.pipe(distinctUntilChanged(isEqual)).subscribe((selectedReport: ReportLaunchOptions) => {
            if (!selectedReport) {
                this.reportTitle.emit(null);
                this.recordCount.emit(0);
            } else {
                this.reportTitle.emit(selectedReport.definition.displayName);
            }
        });
    }

    ngAfterContentChecked() {
        // if (this._selected) {
        //     this.reportTitle.emit(this._selected.definition.displayName);
        //     this.store.dispatch(new SelectReport({selectedReport: this._selected}));
        //
        //     this._selected = null;
        // }
        // if (this._recordCount !== null) {
        //     this.recordCount.emit(this._recordCount);
        // }
    }

    reportCompareFn(c1: ReportDefinition, c2: ReportDefinition) {
        return c1 && c2 && c1.name === c2.name;
    }

    setSelectedReport(selected: ReportDefinition) {
        if (selected) {
            const launchOptions: ReportLaunchOptions = { definition: selected };
            this.store.dispatch(new SelectReport({ selectedReport: launchOptions }));
        }
    }

    setRecordCount(count: number) {
        this.recordCount.emit(count);
    }

    clearReports() {
        this.reportTitle.emit(null);
        this.setRecordCount(0);
    }

    createUid(...args: any[]) {
        return args.join('_');
    }

    get layer() {
        if (this.reportType === 'feature' && this.launchOptions.length > 0) {
            return this.launchOptions[0].definition.layer;
        }
        return null;
    }

    get objectKey() {
        return this.isolatedReport.objectKey;
    }

    get visibleDefinitions(): ReportDefinition[] {
        return this.launchOptions.map((o) => o.definition).filter((d) => d && !d.hidden);
    }

    get selectedReport$(): Observable<ReportLaunchOptions> {
        switch (this.reportType.toLowerCase()) {
            case 'feature':
                return this.selectedFeatureReports$.pipe(map((fn) => fn(this.launchOptions[0].definition.layer)));
            case 'data':
                return this.selectedDataReport$.pipe(filter((obj) => !!obj));
        }
    }

    get selectedReportDefinition$(): Observable<ReportDefinition> {
        return this.selectedReport$.pipe(map((r) => r && r.definition));
    }

    get digitizeKey$() {
        return this.isolatedReport.digitizeKey$;
    }
}
